import { UTCDate } from '@/utils/dates';
import { AccountView } from '../../../types/entity-types';
import { filterInternalTransfers } from './filter-internal-transfers';
import { ReportingTotals } from './get-reporting-data';
import { getReportingTimeframe } from './get-reporting-timeframe';

export type ReportingBalances = {
  month3: { start: number; end: number };
  month2: { start: number; end: number };
  month1: { start: number; end: number };
  month0: { start: number; end: number };
  total: { start: number; end: number };
};

export const getReportingBalances = (
  accounts: AccountView[],
  totals: ReportingTotals['totals'],
  referenceDate: Date = new UTCDate(),
): ReportingBalances => {
  const { startReportingDate } = getReportingTimeframe({ referenceDate });

  // Single pass: calculate current balance and net flow after start date
  // Only exclude Internal Transfers to match table calculations
  // Transactions without vendor data are included
  const { currentBalance, netFlowAfterStart } = accounts.reduce(
    (acc, account) => {
      const accountBalance = account.baseCurrencyAvailable || 0;

      // Calculate net flow for transactions after start date
      // IMPORTANT: Exclude internal transfers to match totals calculation
      const accountTransactions = account.transactions || [];
      const transactionsAfterStart = accountTransactions.filter((transaction) => {
        if (!transaction.fspDate) return false;
        const transactionDate = new UTCDate(transaction.fspDate);
        return transactionDate >= startReportingDate;
      });

      // Filter out internal transfers to match the totals calculation
      const filteredTransactions = filterInternalTransfers(transactionsAfterStart);

      const accountNetFlow = filteredTransactions.reduce((sum, transaction) => {
        const amount = transaction.baseCurrencyAmount || 0;
        return sum + (transaction.isCredit ? amount : -amount);
      }, 0);

      return {
        currentBalance: acc.currentBalance + accountBalance,
        netFlowAfterStart: acc.netFlowAfterStart + accountNetFlow,
      };
    },
    { currentBalance: 0, netFlowAfterStart: 0 },
  );

  // Calculate initial balance at start of reporting period
  // Current balance minus net flow after start = balance at start date
  const initialStartBalance = currentBalance - netFlowAfterStart;

  // Calculate period balances using the totals from the table to ensure continuity
  const m3Start = initialStartBalance;
  const m3End = m3Start + totals.threeMonthsAgoAmount;

  const m2Start = m3End;
  const m2End = m2Start + totals.twoMonthsAgoAmount;

  const m1Start = m2End;
  const m1End = m1Start + totals.previousMonthAmount;

  const m0Start = m1End;
  const m0End = m0Start + totals.currentMonthAmount;

  return {
    month3: { start: m3Start, end: m3End },
    month2: { start: m2Start, end: m2End },
    month1: { start: m1Start, end: m1End },
    month0: { start: m0Start, end: m0End },
    total: { start: m3Start, end: m0End },
  };
};
